import React, { useEffect, useState } from "react";
import { Save, X } from "lucide-react";

import { FormLayout } from "../../../../components/layout/forms/FormLayout";
import FormHeader from "../../../../components/layout/forms/header/FormHeader";

import Text from "../../../../components/typography/Text";
import Button from "../../../../components/buttons/Button";

import TextField from "../../../../components/input/textfield/TextField";
import Select from "../../../../components/input/select/Select";

import { useCreatorPops } from "../hooks/useCreatorPops";

import { validateRequiredFields } from "../../../../utils/helpers/validation/validateRequiredFields";

import "./CreatorPopEditForm.css";

const VISIBILITY_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Private", value: "private" },
];

const VALID_VISIBILITY = new Set(
  VISIBILITY_OPTIONS.map((option) => option.value),
);

export default function CreatorPopEditForm({
  popTag,
  username,
  onClose,
  onCancel,
  onSuccess,
}) {
  const {
    loadingEdit,
    error,
    saving,
    getCreatorPopDetails,
    updateCreatorPopDetails,
  } = useCreatorPops(username);

  const [popDetails, setPopDetails] = useState(null);
  const [submitError, setSubmitError] = useState("");

  const loading = loadingEdit || saving;

  const visibility = String(popDetails?.visibility || "private")
    .trim()
    .toLowerCase();

  const errorMessage =
    submitError || (typeof error === "string" ? error : error?.message || "");

  useEffect(() => {
    if (!popTag || !username) {
      setPopDetails(null);
      return;
    }

    let cancelled = false;

    const loadPop = async () => {
      setPopDetails(null);
      setSubmitError("");

      try {
        const data = await getCreatorPopDetails(popTag, username);

        if (cancelled) return;

        if (!data) {
          throw new Error("Unable to load Pop details.");
        }

        const response = data?.data ?? data;

        if (response?.success === false) {
          throw new Error(response.message || "Unable to load Pop details.");
        }

        const pop = response?.pop;

        if (!pop || typeof pop !== "object") {
          throw new Error("Pop details are unavailable.");
        }

        setPopDetails({
          ...pop,
          title: String(pop.title || ""),
          tags: String(pop.tags || ""),
          visibility: String(pop.visibility || "private")
            .trim()
            .toLowerCase(),
        });
      } catch (loadError) {
        if (cancelled) return;

        console.error("Unable to load creator Pop edit details:", loadError);

        setSubmitError(
          loadError?.response?.data?.message ||
            loadError?.message ||
            "Unable to load Pop details.",
        );
      }
    };

    void loadPop();

    return () => {
      cancelled = true;
    };
  }, [popTag, username]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setPopDetails((previous) => ({
      ...(previous || {}),
      [name]: value,
    }));

    setSubmitError("");
  };

  const handleClose = () => {
    if (loading) return;

    if (typeof onClose === "function") {
      onClose();
    } else {
      onCancel?.(false);
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (loading || !popDetails) return;

    setSubmitError("");

    const requiredResult = validateRequiredFields(popDetails, [
      "title",
      "visibility",
    ]);

    if (!requiredResult.isValid) {
      setSubmitError("Please fill in all required fields.");
      return;
    }

    const title = String(popDetails.title || "").trim();
    const tags = String(popDetails.tags || "").trim();

    if (!title) {
      setSubmitError("Pop title is required.");
      return;
    }

    if (title.length > 255) {
      setSubmitError("Pop title cannot exceed 255 characters.");
      return;
    }

    if (!VALID_VISIBILITY.has(visibility)) {
      setSubmitError("Select Public, Unlisted or Private visibility.");
      return;
    }

    const payload = {
      title,
      tags,
      visibility,
    };

    try {
      const result = await updateCreatorPopDetails(popTag, payload);

      if (result === false || result?.success === false) {
        throw new Error(result?.message || "Unable to update Pop details.");
      }

      if (typeof onSuccess === "function") {
        await onSuccess(result);
      }

      handleClose();
    } catch (saveError) {
      console.error("Unable to save creator Pop:", saveError);

      setSubmitError(
        saveError?.response?.data?.message ||
          saveError?.message ||
          "Unable to save Pop changes.",
      );
    }
  };

  return (
    <FormLayout className="creator-pop-edit-form">
      <FormHeader
        title="Edit Pop"
        description="Update your Pop details and visibility."
      />

      {errorMessage && (
        <div className="creator-pop-edit-form__error" role="alert">
          <Text as="p" size="sm">
            {errorMessage}
          </Text>
        </div>
      )}

      {!popDetails && loadingEdit && (
        <div className="creator-pop-edit-form__loading" role="status">
          <Text as="p" tone="secondary" size="sm">
            Loading Pop details...
          </Text>
        </div>
      )}

      {popDetails && (
        <div className="creator-pop-edit-form__fields">
          <TextField
            label="Title"
            name="title"
            value={popDetails.title}
            onChange={handleInputChange}
            required
            disabled={loading}
            placeholder="Enter Pop title"
          />

          <TextField
            label="Tags"
            name="tags"
            value={popDetails.tags}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="Enter tags"
          />

          <Select
            label="Visibility"
            name="visibility"
            value={visibility}
            onChange={handleInputChange}
            items={VISIBILITY_OPTIONS}
            required
            disabled={loading}
          />

          <Text as="p" size="xs" tone="secondary">
            {visibility === "public"
              ? "This Pop is available publicly."
              : visibility === "unlisted"
                ? "Only people with access to this Pop can view it."
                : "Only you can access this Pop."}
          </Text>
        </div>
      )}

      <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
        <Button
          value="Cancel"
          variant="primary-outlined"
          icon={<X size={16} />}
          onClick={handleClose}
          disabled={loading}
        />

        <Button
          value={saving ? "Saving..." : "Save Changes"}
          variant="primary"
          icon={<Save size={16} />}
          onClick={handleSubmit}
          disabled={loading || !popDetails}
        />
      </div>
    </FormLayout>
  );
}
